import { useState } from "react"
import { toast } from "sonner"
import { MoreHorizontal, Plus } from "lucide-react"
import {
  useOrgUsers,
  useInviteOrgUser,
  useUpdateOrgUser,
  useSendOrgUserReset,
  useUnlockOrgUser,
} from "@/lib/queries"
import type { OrgUser } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table"

export function OrgUsersPanel({
  currentUserId,
  subOrgId,
}: {
  currentUserId: number
  subOrgId?: number
}) {
  const { data: users, isLoading, isError } = useOrgUsers(subOrgId)
  const inviteUser = useInviteOrgUser(subOrgId)
  const updateUser = useUpdateOrgUser(subOrgId)
  const sendReset = useSendOrgUserReset(subOrgId)
  const unlockUser = useUnlockOrgUser(subOrgId)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [role, setRole] = useState("staff")

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await inviteUser.mutateAsync({ email, role })
      setInviteOpen(false)
      setEmail("")
      setRole("staff")
      toast.success("Invite sent")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not invite user")
    }
  }

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      toast.success(ok)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed")
    }
  }

  if (isLoading) return <p className="text-muted-foreground py-6">Loading...</p>
  if (isError) return <p className="text-destructive py-6">Could not load users.</p>

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Invite user
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last sign-in</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users ?? []).map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isSelf={u.id === currentUserId}
                onToggleActive={() =>
                  act(
                    () => updateUser.mutateAsync({ id: u.id, active: !u.active }),
                    u.active ? "User locked" : "User unlocked"
                  )
                }
                onToggleRole={() =>
                  act(
                    () =>
                      updateUser.mutateAsync({
                        id: u.id,
                        role: u.role === "manager" ? "staff" : "manager",
                      }),
                    "Role updated"
                  )
                }
                onSendReset={() =>
                  act(
                    () => sendReset.mutateAsync(u.id),
                    u.sign_in_count === 0 ? "Invite re-sent" : "Password reset sent"
                  )
                }
                onClearLock={() =>
                  act(() => unlockUser.mutateAsync(u.id), "Login lock cleared")
                }
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={inviteUser.isPending}>
                Send invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function UserRow({
  user: u,
  isSelf,
  onToggleActive,
  onToggleRole,
  onSendReset,
  onClearLock,
}: {
  user: OrgUser
  isSelf: boolean
  onToggleActive: () => void
  onToggleRole: () => void
  onSendReset: () => void
  onClearLock: () => void
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {u.username}
        {isSelf && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
      </TableCell>
      <TableCell>{u.email}</TableCell>
      <TableCell>{u.role === "manager" ? "Manager" : "Staff"}</TableCell>
      <TableCell>
        {!u.active ? (
          <Badge variant="destructive">Locked</Badge>
        ) : u.locked ? (
          <Badge variant="outline">Login locked</Badge>
        ) : (
          <Badge variant="secondary">Active</Badge>
        )}
      </TableCell>
      <TableCell>
        {u.last_sign_in_at
          ? new Date(u.last_sign_in_at).toLocaleDateString()
          : "Never"}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!isSelf && (
              <DropdownMenuItem onClick={onToggleActive}>
                {u.active ? "Lock account" : "Unlock account"}
              </DropdownMenuItem>
            )}
            {u.locked && (
              <DropdownMenuItem onClick={onClearLock}>
                Clear login lock
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onSendReset}>
              {u.sign_in_count === 0 ? "Re-send invite" : "Send password reset"}
            </DropdownMenuItem>
            {!isSelf && (
              <DropdownMenuItem onClick={onToggleRole}>
                {u.role === "manager" ? "Change to staff" : "Make manager"}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}
